import { createSlice } from '@reduxjs/toolkit';

import { RejectedActionFromAsyncThunk } from '@/redux/createAsyncThunk';
import {
    addPlaidBankAccount,
    fetchBankAccounts,
    fetchBidderPlaidLinkToken,
    removeBankAccount,
} from '@/redux/modules/account/payments/bankAccount/bankAccount.actions';
import {
    BankAccountSlice,
    defaultBankAccountSlice,
} from '@/redux/modules/account/payments/bankAccount/bankAccount.types';

const bankAccountPendingReducer = (slice: BankAccountSlice): BankAccountSlice => ({
    ...slice,
    bankAccountsLoaded: 0,
    bankAccountsLoading: true,
    errorMessage: '',
});

const bankAccountFailedReducer = (
    slice: BankAccountSlice,
    {
        error,
    }: RejectedActionFromAsyncThunk<typeof removeBankAccount> | RejectedActionFromAsyncThunk<typeof fetchBankAccounts>
): BankAccountSlice => ({
    ...slice,
    bankAccountsLoading: false,
    errorMessage: error.message,
});

const plaidPendingReducer = (slice: BankAccountSlice): BankAccountSlice => ({
    ...slice,
    plaidError: false,
    plaidFetching: true,
    plaidSuccess: false,
});

const plaidFailedReducer = (slice: BankAccountSlice): BankAccountSlice => ({
    ...slice,
    plaidError: true,
    plaidFetching: false,
    plaidSuccess: false,
});

const bankAccountSlice = createSlice({
    extraReducers: (builder) => {
        builder.addCase(fetchBankAccounts.pending, bankAccountPendingReducer);
        builder.addCase(fetchBankAccounts.fulfilled, (slice, { payload }) => {
            slice.bankAccounts = payload;
            slice.bankAccountsLoaded = Date.now();
            slice.bankAccountsLoading = false;
            slice.errorMessage = '';
        });
        builder.addCase(fetchBankAccounts.rejected, bankAccountFailedReducer);

        builder.addCase(removeBankAccount.pending, bankAccountPendingReducer);
        builder.addCase(removeBankAccount.fulfilled, (slice) => {
            slice.bankAccounts = [];
            slice.errorMessage = '';
        });
        builder.addCase(removeBankAccount.rejected, bankAccountFailedReducer);

        builder.addCase(fetchBidderPlaidLinkToken.pending, plaidPendingReducer);
        builder.addCase(fetchBidderPlaidLinkToken.fulfilled, (slice, { meta, payload }) => {
            slice.plaidError = false;
            slice.plaidFetching = false;
            slice.plaidSuccess = true;
            slice.plaidLinkTokens = {
                ...slice.plaidLinkTokens,
                [meta.arg.accessToken ?? '']: payload,
            };
        });
        builder.addCase(fetchBidderPlaidLinkToken.rejected, plaidFailedReducer);

        builder.addCase(addPlaidBankAccount.pending, plaidPendingReducer);
        builder.addCase(addPlaidBankAccount.fulfilled, (slice, { meta, payload }) => {
            // Remove the old pending account and then add the new version
            const bankAccounts = slice.bankAccounts.filter((account) => account.plaidToken !== meta.arg.accessToken);
            bankAccounts.push(payload);

            slice.plaidError = false;
            slice.plaidFetching = false;
            slice.plaidSuccess = true;
            slice.bankAccounts = bankAccounts;
        });
        builder.addCase(addPlaidBankAccount.rejected, plaidFailedReducer);
    },
    initialState: defaultBankAccountSlice,
    name: 'bankAccount',
    reducers: {},
});

export const { reducer: bankAccountReducer } = bankAccountSlice;
